import React, { useEffect, useState, useCallback, Fragment } from 'react'
import {
  Container,
  FormControlLabel,
  Switch,
  Typography
} from '@mui/material'
import {
  getInboundAjustWorkOrder,
  getInboundAjustWorkOrderLpn
} from '../config/api'
import { AsnAdjustDetail } from '../../../interfaces/Inbound/InboundAdjust'
import { AsnAdjustWorkOrderLpn } from '../../../interfaces/AsnAdjust/AsnAdjust'
import { ApiResponse } from '../../../typings/ApiResponse'
import { Modal } from '../../../components/_EOS/Modal'
import { Loading } from '../../../components/UI'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { Pagination } from '../../../components/Pagination'
import { ModalReturnType } from '../../../hooks/useModal'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { usePagination } from '../../../hooks/usePagination'
import { UnifiedGrid } from './UnifiedGrid'

interface ASNAdjustmentModalProps {
  asnAdjustmentModalState: ModalReturnType<{
    workOrderId: number
    asnNumber: string
  }>
  handleSnackbarNotification: (msg: string, success: boolean) => void
  refetchGridData: () => void
}

export const ASNAdjustmentModalNew: React.FC<ASNAdjustmentModalProps> = ({
  asnAdjustmentModalState,
  refetchGridData
}) => {
  const { state: inboundAdjustApiState, callApi: InboundAdjustWorkOrder } =
    useApi<ApiResponse<AsnAdjustDetail>>()
  const {
    state: inboundAdjustLpnApiState,
    callApi: InboundAdjustLpnWorkOrder
  } = useApi<ApiResponse<AsnAdjustWorkOrderLpn>>()
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const pagination = usePagination(5)
  const [loading, setLoading] = useState<boolean>(false)
  const [showName, setShowName] = useState<boolean>(false)
  const [showAdjusted, setShowAdjusted] = useState<boolean>(false)

  const lpnItems = inboundAdjustLpnApiState.data?.items
  const skuItems = inboundAdjustApiState.data?.items

  const hasLpns = lpnItems && lpnItems.length > 0

  const fetchInboundAdjust = (
    WorkOrderid: number,
    AdjustedDetails: boolean,
    page: number,
    pageSize: number
  ) => {
    setLoading(true)
    return InboundAdjustWorkOrder(
      getInboundAjustWorkOrder(WorkOrderid, page, pageSize, AdjustedDetails)
    )
      .then((result) => result)
      .catch((error) => {
        openSnackBar(`Error al cargar datos: ${error.message}`, false)
        throw error
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const fetchInboundAdjustLpn = (
    WorkOrderid: number,
    page: number,
    pageSize: number
  ) => {
    setLoading(true)
    return InboundAdjustLpnWorkOrder(
      getInboundAjustWorkOrderLpn(WorkOrderid, page, pageSize)
    )
      .catch((error) => {
        openSnackBar(`Error al cargar datos: ${error.message}`, false)
        throw error
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const workOrderId = asnAdjustmentModalState.extendedProps?.workOrderId

  const loadInitialData = useCallback(async () => {
    if (workOrderId) {
      try {
        setLoading(true)

        // Cargar datos según si hay LPNs o no
        if (hasLpns) {
          await fetchInboundAdjustLpn(workOrderId, 0, pagination.pageSize)
        } else {
          await fetchInboundAdjust(
            workOrderId,
            showAdjusted,
            0,
            pagination.pageSize
          )
        }

        pagination.setPagination(0, pagination.pageSize)
      } catch (error) {
        console.error('Falló la carga de datos:', error)
      } finally {
        setLoading(false)
      }
    }
  }, [workOrderId, pagination.pageSize, hasLpns, showAdjusted])

  useEffect(() => {
    if (asnAdjustmentModalState.isOpen) {
      loadInitialData()
    }
  }, [asnAdjustmentModalState.isOpen, workOrderId, showAdjusted])

  const onCloseModal = () => {
    asnAdjustmentModalState.closeModal()
    refetchGridData()
  }

  const handlePagination = (page: number, pageSize: number) => {
    if (workOrderId) {
      if (hasLpns) {
        fetchInboundAdjustLpn(workOrderId, page, pageSize)
      } else {
        fetchInboundAdjust(workOrderId, showAdjusted, page, pageSize)
      }
      pagination.setPagination(page, pageSize)
    }
  }

  const handleDisplayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowName(event.target.checked)
  }

  const handleShowAdjustedChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setShowAdjusted(event.target.checked)
  }

  // Determinar qué datos mostrar
  const dataItems = hasLpns ? lpnItems || [] : skuItems || []

  return (
    <Container>
      <Modal
        open={asnAdjustmentModalState.isOpen}
        customTitle={
          <Typography variant='h6' style={{ fontWeight: 'bold' }}>
            {`AJUSTAR ASN: ${asnAdjustmentModalState.extendedProps?.asnNumber}`}
          </Typography>
        }
        onClose={onCloseModal}
        fullWidth={true}
        maxWidth={'xl'}
      >
        {loading ? (
          <Loading />
        ) : (
          <Fragment>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAdjusted}
                    onChange={handleShowAdjustedChange}
                    name='checkedB'
                    color='primary'
                  />
                }
                label={'Mostrar Gestionados'}
              />
            </div>
            <UnifiedGrid
              items={dataItems}
              workOrderId={workOrderId || 0}
              hasLpns={hasLpns || false}
              showName={showName}
              onDisplayChange={handleDisplayChange}
              onLineSaved={loadInitialData}
              externalOpenSnackBar={openSnackBar}
            />
            {dataItems.length > 0 && (
              <Pagination
                pagination={{
                  totalItem: hasLpns
                    ? inboundAdjustLpnApiState.data?.totalItems || 0
                    : inboundAdjustApiState.data?.totalItems || 0,
                  page: pagination.page,
                  pageSize: pagination.pageSize
                }}
                onPaginationChange={handlePagination}
              />
            )}
          </Fragment>
        )}
        <SnackBar
          message={message}
          status={isSuccess ? 'success' : 'error'}
          open={isActive}
        />
      </Modal>
    </Container>
  )
}
